import { Phone, PhoneOff, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import type { Company } from "@/services/companyApi";
import { Alert, AlertDescription } from "./ui/alert";

type CallStatus =
  | "idle"
  | "initiating"
  | "ringing"
  | "in-progress"
  | "completed"
  | "failed"
  | "busy"
  | "no-answer";

type OutboundCallPanelProps = {
  phoneNumber: string;
  setPhoneNumber: (number: string) => void;
  callStatus: CallStatus;
  onInitiateCall: () => void;
  onEndCall: () => void;
  selectedCompanyId: string | null;
  setSelectedCompanyId: (companyId: string) => void;
  companies: Company[];
  companiesLoading: boolean;
  callDuration?: number;
  error?: string | null;
};

export default function OutboundCallPanel({
  phoneNumber,
  setPhoneNumber,
  callStatus,
  onInitiateCall,
  onEndCall,
  selectedCompanyId,
  setSelectedCompanyId,
  companies,
  companiesLoading,
  callDuration,
  error,
}: OutboundCallPanelProps) {
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    // Format as E.164 (e.g., +14165551234)
    if (digits.length > 0) {
      return `+${digits}`;
    }
    return "";
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getStatusBadge = () => {
    switch (callStatus) {
      case "idle":
        return (
          <Badge variant="secondary" className="ml-2">
            Ready
          </Badge>
        );
      case "initiating":
        return (
          <Badge variant="default" className="ml-2">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Initiating...
          </Badge>
        );
      case "ringing":
        return (
          <Badge variant="default" className="ml-2">
            Ringing...
          </Badge>
        );
      case "in-progress":
        return (
          <Badge variant="default" className="ml-2 bg-green-600">
            <span className="animate-pulse mr-1">●</span>
            Active
            {callDuration !== undefined && ` - ${formatDuration(callDuration)}`}
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="secondary" className="ml-2">
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive" className="ml-2">
            Failed
          </Badge>
        );
      case "busy":
        return (
          <Badge variant="secondary" className="ml-2">
            Busy
          </Badge>
        );
      case "no-answer":
        return (
          <Badge variant="secondary" className="ml-2">
            No Answer
          </Badge>
        );
      default:
        return null;
    }
  };

  const isCallActive = ["initiating", "ringing", "in-progress"].includes(
    callStatus
  );
  const canInitiateCall =
    callStatus === "idle" &&
    phoneNumber.length > 5 &&
    selectedCompanyId !== null;

  return (
    <div className="p-6 border rounded-lg flex flex-col gap-6 bg-card">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Outbound Call</h2>
        {getStatusBadge()}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 pl-1 mb-2">
            Company
          </label>
          <Select
            value={selectedCompanyId ?? ""}
            onValueChange={setSelectedCompanyId}
            disabled={isCallActive}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  companiesLoading ? "Loading companies..." : "Select company"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 pl-1 mb-2">
            Phone Number (E.164 format)
          </label>
          <Input
            type="tel"
            placeholder="+14165551234"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            disabled={isCallActive}
            className="font-mono"
          />
          <p className="text-xs text-gray-500 mt-1 pl-1">
            Enter number with country code (e.g., +1 for US/Canada)
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          {!isCallActive ? (
            <Button
              onClick={onInitiateCall}
              disabled={!canInitiateCall}
              className="flex-1"
              size="lg"
            >
              <Phone className="w-4 h-4 mr-2" />
              Initiate Call
            </Button>
          ) : (
            <Button
              onClick={onEndCall}
              variant="destructive"
              className="flex-1"
              size="lg"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              End Call
            </Button>
          )}
        </div>
      </div>

      {isCallActive && (
        <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
          <p>
            Calling <span className="font-mono font-semibold">{phoneNumber}</span>
          </p>
        </div>
      )}
    </div>
  );
}
